import type { ReactNode } from 'react';

import { decimal, num } from '../format';

/**
 * Ranks, XP, the verdict on a check, the hint ladder's price, the nudges
 * when somebody is stuck, and what a locked level says
 * (progress.ts, lib/grade.ts, lib/entitlement.ts, state/useStuck.ts,
 * components/play/ClearOverlay.tsx, HintLadder.tsx, Unstuck.tsx, SandboxRail.tsx).
 *
 * See web/src/lib/strings/index.ts for how this file is shaped and why, and
 * docs/i18n.md for the Arabic glossary every value here must follow. Neither
 * object may be `as const`: `ar` is typed against `en`, and literal types
 * would make it have to repeat the English words.
 *
 * ## This is the domain the plurals break
 *
 * Almost every sentence here has a number in it — objectives held, hints
 * spent, seconds elapsed, levels in the paid half — and a good half of them
 * are the kind of sentence English writes by sticking an `s` on a noun.
 * Arabic has six categories to English's two and the dual is obligatory: two
 * objectives are `هدفان`, not `أهداف` with a 2 in front. So nothing in this
 * file hands a component a fragment to join. `verdict` is three functions
 * that each return a finished sentence, the hidden-check clause included,
 * because a clause appended after the fact lands in the wrong place in a
 * language whose word order is not English's.
 *
 * ## Where the callers are, and which door they come in by
 *
 * Four of the readers are not components — `progress.ts`, `lib/grade.ts`,
 * `lib/entitlement.ts` and `state/useStuck.ts` all build sentences outside
 * the render tree — so they read this through `getStrings()`. The four
 * `components/play/*` files use the `useStrings()` hook. Getting that
 * backwards freezes the language rather than failing loudly, which is why
 * `getStrings` carries the warning it does.
 */

/* ── Arabic agreement ─────────────────────────────────────────────────────── */

interface Forms {
  /** One. Arabic names the thing and drops the numeral: `هدف واحد`. */
  one: string;
  /** Two. The dual, not `2` plus a plural: `هدفان`. */
  two: string;
  /** 3–10, where the counted noun is a broken plural in the genitive. */
  few: string;
  /** 11–99, where it goes back to an accusative singular. */
  many: string;
  /** 0, 100, 101 … — the singular again. */
  other: string;
}

/**
 * Pick the Arabic form a count agrees with. The same rule as play.ts's, kept
 * per file rather than shared so that a domain stays readable whole.
 */
function plural(n: number, forms: Forms): string {
  const rest = n % 100;
  if (n === 1) return forms.one;
  if (n === 2) return forms.two;
  if (rest >= 3 && rest <= 10) return forms.few;
  if (rest >= 11 && rest <= 99) return forms.many;
  return forms.other;
}

/**
 * `هدف واحد`, `هدفان`, `5 أهداف`, `15 هدفًا`, `100 هدف` — the counted phrase
 * the verdict is built out of.
 *
 * `genitive` is for the form after a preposition, where only the dual
 * changes: `من هدفين`, never `من هدفان`. Nothing else in the set inflects, so
 * one flag covers it.
 */
function objectives(n: number, genitive = false): string {
  return plural(n, {
    one: 'هدف واحد',
    two: genitive ? 'هدفين' : 'هدفان',
    few: `${num(n, 'ar')} أهداف`,
    many: `${num(n, 'ar')} هدفًا`,
    other: `${num(n, 'ar')} هدف`,
  });
}

/**
 * `سؤال واحد`, `سؤالان`, `5 أسئلة`, `15 سؤالًا`, `100 سؤال` — the questions an
 * account may still put to the model mentor.
 *
 * `genitive` is the same flag `objectives` carries and for the same reason:
 * the allowance line says `من` before it, and `من سؤالان` is not Arabic.
 */
function arQuestions(n: number, genitive = false): string {
  return plural(n, {
    one: 'سؤال واحد',
    two: genitive ? 'سؤالين' : 'سؤالان',
    few: `${num(n, 'ar')} أسئلة`,
    many: `${num(n, 'ar')} سؤالًا`,
    other: `${num(n, 'ar')} سؤال`,
  });
}

/**
 * `فحصًا واحدًا`, `فحصين`, `3 فحوصات`, `15 فحصًا` — what an attempt cost, as
 * the object of `استغرق`.
 *
 * Accusative throughout, because the only sentence that counts checks is the
 * debrief's, and there the count is what the attempt took. That is where the
 * dual shows (`فحصين`, never `فحصان`) and where the 11–99 form does.
 */
function arChecks(n: number): string {
  return plural(n, {
    one: 'فحصًا واحدًا',
    two: 'فحصين',
    few: `${num(n, 'ar')} فحوصات`,
    many: `${num(n, 'ar')} فحصًا`,
    other: `${num(n, 'ar')} فحص`,
  });
}

/** The second sentence of a failing verdict, when a check the learner cannot see also failed. */
const HIDDEN_AR = ' وأخفق فحص خفي؛ انظر قائمة الأهداف.';

/**
 * `المستوى 33`, `المستويين 30 و33`, `المستويات 30 و31 و33` — the levels a
 * refusal names, as one phrase.
 *
 * The numbers here are *names*, not a count: the sentence is not saying how
 * many levels there are, it is listing which ones. So the counted-noun rules
 * that `objectives` above has to obey do not apply, and only the two forms
 * Arabic makes obligatory move — the singular and the dual. Three and thirty
 * are both `المستويات`, the way `allHold` keeps one string for 3-10 and
 * 11-99.
 *
 * Joined with ` و` and no commas, which is how Arabic writes a list. That
 * also settles the direction question without an isolate: every number is
 * fenced by Arabic letters, so the bidi algorithm never has two digit runs
 * separated by a neutral to decide about. `ltr()` is for the other shape —
 * `format.ts`'s `5.5 MB`, where a space sits between digits and a Latin word.
 */
function levelsNamed(levels: number[]): string {
  const names = levels.map((l) => num(l, 'ar')).join(' و');
  return plural(levels.length, {
    one: `المستوى ${names}`,
    two: `المستويين ${names}`,
    few: `المستويات ${names}`,
    many: `المستويات ${names}`,
    other: `المستويات ${names}`,
  });
}

/**
 * `المستوى الوحيد`, `المستويين`, `المستويات الـ 20` — a whole track's levels,
 * as the object of `افتح`.
 *
 * The definite apposition (`المستويات الـ 20`) is the construction `allHold`
 * uses for the same reason: the noun is already definite, so the figure sits
 * beside it rather than governing it, and it does not inflect between 3-10
 * and 11-99. One and two are named rather than counted, because `المستوى
 * الـ 1` is not a phrase anybody writes.
 */
function pathLevels(n: number): string {
  return plural(n, {
    one: 'المستوى الوحيد',
    two: 'المستويين',
    few: `المستويات الـ ${num(n, 'ar')}`,
    many: `المستويات الـ ${num(n, 'ar')}`,
    other: `المستويات الـ ${num(n, 'ar')}`,
  });
}

/* ── English lists ────────────────────────────────────────────────────────── */

/**
 * `33`, `30 and 33`, `30, 31 and 33`. No serial comma, as everywhere else in
 * the catalogue ("Lattice, Priya and Marcus are fictional").
 */
function list(parts: string[]): string {
  if (parts.length < 3) return parts.join(' and ');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/* ── English ──────────────────────────────────────────────────────────────── */

export const en = {
  /* --- The ladder of ranks (progress.ts) ---------------------------------- */

  /**
   * Nine rungs, keyed by an id rather than by their own English names,
   * because the XP thresholds beside them in progress.ts are a fixed ladder
   * and the name on a rung is the part that changes language.
   *
   * They are titles, not job grades: what somebody would be pleased to be
   * called. The blurb is one line about what that person can do.
   */
  ranks: {
    intern: { name: 'Intern', blurb: 'First day at Lattice. Everything is new.' },
    junior: { name: 'Junior Engineer', blurb: 'Can change code safely on a branch.' },
    engineer: { name: 'Engineer', blurb: 'Ships tickets end to end, tests included.' },
    senior: { name: 'Senior Engineer', blurb: 'Recovers from anything. Reviews others.' },
    owner: { name: 'Service Owner', blurb: 'Owns a service and its history.' },
    lead: { name: 'Tech Lead', blurb: "Reviews other people's work. Leaves the codebase clearer." },
    staff: { name: 'Staff Engineer', blurb: 'Cuts the release, and answers for it at 3am.' },
    principal: { name: 'Principal Engineer', blurb: 'Automates the checks. Moves data without losing any.' },
    distinguished: { name: 'Distinguished Engineer', blurb: 'Takes an outage from the alert to the postmortem.' },
  },

  /* --- Why a clear could not be vouched for (progress.ts) ------------------ */

  /**
   * The two reasons this file writes itself. They are fragments by design —
   * lowercase, no full stop — because Play joins them into one sentence after
   * a colon (`play.unverified`), in the same register as the engine's own.
   */
  flags: {
    levelSprint: (seconds: number) =>
      `it was cleared ${decimal(seconds, 1, 'en')} seconds after the previous level, and a level cannot be read and solved that fast`,
    tampered: 'the saved progress record did not match its signature',
  },

  /* --- What the rail says after a check (lib/grade.ts) --------------------- */

  /**
   * Three whole sentences, not a stem plus a clause.
   *
   * The hidden-check note used to be concatenated onto the end of whichever
   * count sentence came out, which fixes English's order for every language
   * and makes the count phrase a fragment. Each of these builds the finished
   * detail line instead, so Arabic can agree its noun with the number and put
   * the hidden-check sentence where that sentence goes.
   */
  verdict: {
    passed: 'Passed.',
    notYet: 'Not yet.',
    /** Every visible objective holds. A pass, so there is no hidden failure to mention. */
    allHold: (total: number) => `All ${num(total, 'en')} objectives hold.`,
    noneHold: (total: number, hiddenFailed: boolean) =>
      `None of the ${num(total, 'en')} objectives hold yet.` +
      (hiddenFailed ? ' A hidden check failed; see the objectives.' : ''),
    someHold: (passed: number, total: number, hiddenFailed: boolean) =>
      `${num(passed, 'en')} of ${num(total, 'en')} objectives hold.` +
      (hiddenFailed ? ' A hidden check failed; see the objectives.' : ''),
  },

  /* --- Why a level will not open (lib/entitlement.ts) ---------------------- */

  locked: {
    signin: (level: number) => `Level ${num(level, 'en')} keeps your progress on your account. Sign in to carry on — the levels you have already done come with you.`,
    /**
     * The levels this one is built on, named.
     *
     * This used to read `Clear Level ${level - 1}`, which was a rule about
     * the course written into a sentence about one level: it said that there
     * is exactly one prerequisite and that it is the row above. A mission now
     * names its own (`requires`, docs/curriculum.md §5), there can be several,
     * and an Act III entry level's prerequisite is fifty rows away — so the
     * caller passes the levels and neither language does arithmetic.
     *
     * **The list comes first**, which is the unusual half of this signature
     * and has two reasons. It is the part that varies and the part both
     * languages have to agree with, so it reads as the subject of the
     * sentence; and the catalogue prober in web/test/strings.test.ts invents
     * its arguments, with an array only ever in first place. A shape it
     * cannot call is a string it cannot check.
     *
     * Empty is a real argument and not a caller's mistake. `isUnlocked` fails
     * closed, so a `requires` naming an id that is not in the catalogue
     * refuses the level and leaves nothing to name (lib/entitlement.ts). It
     * is a broken catalogue rather than a learner's problem, so the sentence
     * says the true thing it can say and does not spill the id.
     */
    sequence: (required: number[], level: number) => {
      const head = `Level ${num(level, 'en')} is locked.`;
      if (required.length === 0) return `${head} Something it is built on has not been cleared.`;
      const noun = required.length === 1 ? 'Level' : 'Levels';
      return `${head} Clear ${noun} ${list(required.map((l) => num(l, 'en')))} first.`;
    },
    /**
     * `from` and `to` are the paid half as the catalogue in this browser
     * actually reports it, not a literal. The range moves every time a
     * chapter ships, and a sentence that had it written into it would go
     * quietly wrong in both languages at once.
     *
     * It is the range of *one track* — the one the refused level is in. Every
     * track numbers itself from zero, so levels 20-51 is only a sentence at
     * all when it is clear which lane's twenty and fifty-one are meant, and
     * the lane the learner is looking at is the one they clicked in.
     */
    paywall: (level: number, from: number, to: number) =>
      `Level ${num(level, 'en')} is part of the full course. Unlock levels ${num(from, 'en')}-${num(to, 'en')} to play it.`,
    paywallSignedOut: (level: number, from: number, to: number) =>
      `Level ${num(level, 'en')} is part of the full course. Sign in and unlock levels ${num(from, 'en')}-${num(to, 'en')} to play it.`,
    /**
     * The same offer for a track that is paid from its first level.
     *
     * A specialization track is priced whole, so there is no free half inside
     * it and no boundary for a range to name — and `0-19` would be a run of
     * numbers this learner has already played, in the general track. A count
     * is the honest shape: it says how much is behind the price without
     * quoting numbers that belong to another lane.
     *
     * "This path", not the track's name, and that is a deferral rather than a
     * preference: the tracks have no display names yet, in either language,
     * and inventing six of them here would be putting a name on the screen
     * before anybody has agreed it. When they exist the sentence should say
     * which path it means, and this is the shape that gets there.
     */
    paywallTrack: (level: number, count: number) =>
      `Level ${num(level, 'en')} is part of the full course. Unlock this path’s ${num(count, 'en')} level${count === 1 ? '' : 's'} to play it.`,
    paywallTrackSignedOut: (level: number, count: number) =>
      `Level ${num(level, 'en')} is part of the full course. Sign in and unlock this path’s ${num(count, 'en')} level${count === 1 ? '' : 's'} to play it.`,
  },

  /* --- The line when somebody is stuck (state/useStuck.ts, Unstuck.tsx) ---- */

  /**
   * The coaching voice, and the place it matters most: this is what the
   * product says to somebody four minutes into being stuck. One line each,
   * terse, and never from above — it says what the offer costs (nothing)
   * rather than telling anyone to try harder.
   *
   * The label travels with the text because it finishes its sentence. A
   * language that phrases the offer differently needs the button to agree
   * with it.
   */
  nudge: {
    /**
     * Two minutes on a level with nothing typed, saved or checked (state/useStuck.ts).
     * `iris` where the level scripts her, else the first rung, free.
     */
    notStarted: {
      iris: {
        text: 'Not sure where to begin? Iris can take the first step with you — she proposes each command, and you decide whether it runs.',
        label: 'Start with Iris',
      },
      hint: {
        text: 'Not sure where to begin? The first hint points the way, and this one is on the house.',
        label: 'Show me',
      },
    },
    ladderSpent: {
      text: 'The answer is on screen and the level still is not passing. Reset the workspace and walk it through, or leave this one and come back to it.',
      label: 'Skip for now',
    },
    neverChecked: {
      text: 'Nothing has been checked yet on this level. Check grades what is there now and names the part that is not done — it costs nothing and gives nothing away.',
      label: 'Check it',
    },
    sameResult: {
      text: 'Three checks, same result. A hint here is a question, not the answer — and this one is on the house.',
      label: 'Show me',
    },
    errorStreak: {
      text: 'Every command for a while now has ended in an error. Want a pointer? It costs nothing this time.',
      label: 'Show me',
    },
    goneQuiet: {
      text: 'Still nothing passing on this level. Being shown where to look costs nothing here, and the level is still yours to solve.',
      label: 'Show me',
    },
    /** Waving the offer away. Not "dismiss": it is a no, not a filing action. */
    dismiss: 'Not now',
  },

  /* --- The clear (components/play/ClearOverlay.tsx) ------------------------ */

  clear: {
    levelWorld: (level: number, world: string) => `Level ${num(level, 'en')} · ${world}`,
    /**
     * A checkpoint's line above the title. It has no level number — it is
     * numbered inside a track of its own, so its "level 0" names nothing a
     * learner has seen — and its catalogue world is the shared "Checkpoints",
     * so it is named by the chapter it assesses, the way the map files it.
     */
    checkpointWorld: (world: string) => `Checkpoint · ${world}`,
    title: 'Level clear',
    checkpointTitle: 'Checkpoint passed',

    /**
     * The unit beside the counting figure, on its own because the figure is
     * a `<Counter>` that animates. English does not inflect it and ignores
     * the count; Arabic has to agree with it.
     */
    xpUnit: (_xp: number) => 'XP',
    xpAlreadyEarned: 'XP · already earned',
    rank: 'Rank',
    meterLabel: 'Progress to next rank',
    rankReached: (rank: string) => `${rank} reached`,
    toNextRank: (xp: number, rank: string) => `${num(xp, 'en')} XP to ${rank}`,
    topRank: 'Top rank reached',
    noteThreeStars: 'Three stars. You completed the level without opening a hint.',
    /**
     * Three stars with a rung open, which the free first rung and the
     * mentor's offered hints both make reachable. The plain version above
     * says "no hints" and would be a flat lie here — and a lie about the
     * scoring is the one kind this panel cannot afford, because the scoring
     * is what the panel is for.
     */
    noteThreeStarsFree: 'Three stars, and the help you took was free. It was always going to be.',
    noteTwoStars: (hintsUsed: number) =>
      `${num(hintsUsed, 'en')} hint${hintsUsed === 1 ? '' : 's'} used. Replay without hints for three stars.`,
    noteOneStar: 'Solved with the solution rung. Come back later and do it from memory.',
    /**
     * In place of the stars on a checkpoint, which has no hint ladder and so
     * nothing for a star to measure. What a pass does earn is the chapter's
     * badge, so that is the sentence — and "every requirement" is exact,
     * because a pass means the ones the brief does not list held too.
     */
    noteCheckpoint: (world: string) => `Every requirement held. The ${world} badge is yours.`,
    readDebrief: 'Read the full debrief',
    /**
     * A chapter is starting, with the chapter's name set in bold in the
     * middle of the sentence — so this is parts rather than a string, and
     * each language decides where the bold lands. Render with `<Parts>`.
     */
    chapterHandover: (level: number, world: ReactNode, minutes: number): ReactNode[] => [
      `Level ${num(level, 'en')} starts a new chapter: `,
      world,
      `. There is a ${num(minutes, 'en')}-minute read on what it is and why it matters — no commands, nothing you need to pass the level.`,
    ],
    nextWorld: (world: string) => `Next · ${world}`,
    straightToLevel: (level: number) => `Straight to level ${num(level, 'en')}`,
    nextLevel: (level: number) => `Next · Level ${num(level, 'en')}`,
    everyLevelCleared: 'Every level cleared',
    levelList: 'Level list',
    stayHere: 'Stay here',
  },

  /* --- What the attempt looked like (components/play/ClearOverlay.tsx) ----- */

  /**
   * What the attempt cost, on the clear overlay.
   *
   * It used to be a list. Every named wrong state the learner had been told
   * they were in was read back here in the level's own words, and these four
   * strings were the furniture around that list. The component that did the
   * telling is gone (docs/mentor.md), so there is no list, and what is left is
   * the two facts about the attempt that are still honestly the learner's own:
   * how many checks it took, and — rarely — the earlier level that went the
   * same way.
   *
   * `earlier` moved here from the mentor's own `recall` cue, which is where
   * the overlay used to borrow it from and where a comment in the component
   * already said it did not belong. It names a level by number and, when the
   * catalogue has it, by title. The title is optional rather than required
   * because a locked or untranslated stub has a number and no name, and a
   * sentence that has to survive that is a sentence that reads correctly on
   * the day it happens.
   */
  debrief: {
    postmortem: {
      open: 'Write a two-line postmortem (optional)',
      note: 'Blameless and private: about the work, not about you. It stays in this browser, and nothing grades it. Putting it in your own words is what turns a failed check into something you keep.',
      wrong: 'What went wrong first?',
      changed: 'What changed it?',
      saved: 'Kept in this browser.',
      unsaved: 'This browser could not keep it.',
    },
    heading: 'Where this went',
    checks: (n: number) => (n === 1 ? 'It took one check.' : `It took ${num(n, 'en')} checks.`),
    /**
     * The memory: an earlier level in the same area that went the same way
     * (docs/mentor-plan.md §3.8). It says that and stops — what to do about it
     * would be the answer to this level arriving by the back door.
     */
    earlier: (level: number, title?: string) =>
      title
        ? `You met this before, on level ${num(level, 'en')}, ${title}. It went the same way then.`
        : `You met this before, on level ${num(level, 'en')}. It went the same way then.`,
    /**
     * The heading over one question carried forward to the next level. It says
     * where the question came from rather than what it is for — "spaced
     * retrieval" is the mechanism's name and not something a learner needs
     * told.
     *
     * **Nothing draws this.** It was written for a question the next level's
     * warm-up would ask again, taken from a situation this learner had been
     * shown (mentor-plan.md §3.10); the panel that showed them is retired and
     * the question it would have carried has no producer left. The key stays
     * because the *heading* is not the mechanism — whatever a warm-up ends up
     * carrying forward will want this sentence — and because deleting a
     * translated string is the kind of change that quietly costs a language.
     */
    retrieval: 'From earlier in the course',
  },

  /* --- Asking a question (components/play/AskMentor.tsx) ------------------ */

  /**
   * The one thing in the help panel that answers a question somebody typed.
   *
   * There were two halves here until recently, and the other one was free: a
   * deterministic mentor that read the working tree on every press, said one
   * thing about it, and never ran out. It is retired (docs/mentor.md), and
   * with it went four button labels and thirty-nine cue phrasings that used to
   * live in this block.
   *
   * What that leaves is a set of sentences about *limits* — an account, an
   * allowance, a deployment switch, a reply that was not allowed out — and
   * limits are where copy goes wrong, because the honest sentence and the
   * defensive one look alike. Three rules, the ones `verification.ts` settled
   * on, and they matter more here now that nothing catches a failed turn:
   *
   *   1. Name which thing refused. A learner who reads "something went wrong"
   *      has been told nothing and will assume it was them.
   *   2. Say when it lifts, where it lifts. An allowance that comes back at a
   *      known time is a wait; one that comes back at no stated time is a wall.
   *   3. End on what is still there. The hint ladder is under every one of
   *      these sentences, costs no account and needs no network, and saying so
   *      is not a consolation prize — it is the accurate next move.
   *
   * And one rule of this milestone's own: **none of them may offer help in
   * place of the answer.** A sentence that shaded into a nudge would be the
   * product pretending a turn worked.
   *
   * None of them says the word "AI", and that is deliberate rather than coy.
   * What the learner is doing is asking a question; which machine answers it
   * is the product's problem and not theirs, and the honest distinction —
   * this costs an allowance and the ladder costs stars — is the one these
   * sentences actually draw.
   *
   * Since 2026-09-28 the one they ask has a name, Iris (docs/iris.md), and
   * the sentences where it is the subject use it. A name is not a category,
   * so the rule above stands. The sentences about limits do not gain it:
   * they are about an allowance or a level, and naming Iris there would put
   * the refusal on the wrong thing.
   */
  mentor: {
    askModel: 'Tell Iris where you are stuck',
    send: 'Ask Iris',
    thinking: 'Thinking…',
    signedOut: 'Asking Iris a question in your own words needs an account. The hint ladder below does not.',
    signIn: 'Sign in',
    /**
     * The standing readout under the field. Two numbers, and in Arabic they
     * agree with different things — hence `arQuestions`, and hence a whole
     * sentence from each language rather than a count handed to a component
     * to append a noun to.
     */
    allowance: (used: number, limit: number) => `${num(used, 'en')} of ${num(limit, 'en')} questions today.`,
    /**
     * The 429, where it is this account's own allowance that is spent.
     *
     * `returnsAt` arrives ready-made from `nextEligibleAt` — a span, a day —
     * so the sentence ends with it and supplies no preposition of its own:
     * "available tomorrow" and "available in three hours" both have to come
     * out as sentences.
     *
     * It is the one failure here a learner can plan around, which is exactly
     * why it must not be said in the same words as the ones they cannot.
     */
    quota: (returnsAt: string) =>
      `Today's questions are used up. The next one is available ${returnsAt}. The hint ladder below is unaffected.`,
    /**
     * The 429 that is not the learner's at all: the deployment has hit its own
     * ceiling for the day or the month.
     *
     * This sentence exists because the alternative is a lie. Both facts arrive
     * as one status code, and telling somebody they have used up their
     * questions when they have used none of them is the single worst thing
     * this line can do — it is the product blaming a learner for its own
     * budget.
     */
    paused: 'Questions are paused here for the moment — this is not your allowance, and nothing of yours was spent. The hint ladder below is unaffected.',
    freeTier: 'The full course comes with the rest of the levels and more room here.',
    /**
     * 403 to an account the mentor does not come with.
     *
     * It is a 403 like the one below and it must not read like one, because
     * nothing is broken and nothing is switched off: the feature is working,
     * on this level, for everybody who bought the course. What this sentence
     * has to do is name the door without making the ladder sound like a
     * consolation — so it says what the ladder actually is, which is five
     * authored rungs on every level and the thing the stars are priced
     * against.
     */
    courseOnly: "Iris's designed steps come with the full course, on the levels where she has them. The hint ladder below is always yours, and it is five rungs deep on every level.",
    /**
     * 429, and the only refusal on this panel that never comes back.
     *
     * The account has used everything the course includes here. It says so
     * plainly rather than softening it, because the failure this sentence
     * exists to avoid is somebody waiting for tomorrow — and it says nothing
     * about money, which is the Worker's business and not something to put in
     * front of a learner mid-level.
     */
    exhausted: 'That is all the help this account has. The hint ladder is unaffected, and it is five rungs deep on every level.',
    /** 403: no switch, no credential, a checkpoint, or a level this tier is not playing. */
    offline: "Questions in your own words aren't part of this level. The hint ladder below works as always.",
    /**
     * A reply came back and the output filter held it.
     *
     * It says a reply existed, because one did and the learner is owed that
     * fact rather than a shrug — and it does not say what was in it, because
     * what the filter catches is the level given away. "Ask about the part you
     * are stuck on" is the honest next move and is not itself a hint: it is
     * advice about the question, not about the level.
     */
    held: 'Iris answered, but the answer said more about this level than it is allowed to. Nothing was charged for it. Asking about the one part you are stuck on usually gets one through.',
    /**
     * The model declined the question itself, which is a different fact from
     * the one above: there is no reply being withheld, so there is nothing to
     * narrow down to. The thing to do is put the question differently.
     */
    declined: 'Iris did not answer that question, and nothing was charged for it. Saying what you tried and what happened instead usually gets an answer.',
    /** 409. The only failure here that resolves by itself, so it says so. */
    inFlight: 'Iris is still answering a question of yours — in this tab or another. Wait for that one to come back before asking again.',
    /**
     * The transport, an abort, a body that is not JSON, or a turn lost at the
     * far end. The one failure that cannot name what went wrong, because
     * nothing came back to name it with — so it says what it does know, which
     * is that nothing was spent and that trying again is free.
     */
    failed: 'No answer came back and nothing was charged for it. Ask it again, or take the ladder below — that one never leaves the tab.',
    /**
     * Who said which turn, for a screen reader and for nobody else.
     *
     * These two were a visible heading over the one reply the panel used to
     * hold — "In reply to your question", which is what marked it as an
     * answer rather than more of the panel's own furniture. A transcript does
     * that job by itself: the learner's own words are directly above the
     * answer to them. So the heading became a label, and the label is
     * `.sr-only`. Since the helper has a name it says who answered, which is
     * the one thing a label on a turn is for.
     *
     * A reader coming down this rail gets "You asked … Iris answered …" in
     * alternation, which is the shape of the thing. Losing
     * that to two visually distinct paragraphs is the ordinary way a
     * transcript becomes unreadable without a screen.
     */
    youAsked: 'You asked',
    answered: 'Iris answered',
    /**
     * The one line about the conversation itself, under the transcript.
     *
     * It says the promise rather than the consequence. Nothing typed here is
     * stored anywhere — not on the server, which has no column for it, and
     * not in this browser either — and the reload that loses the conversation
     * is what that promise looks like from the inside. Said the other way
     * round ("you will lose this if you reload") it reads as a defect to work
     * around, and the first thing it would teach is that reloading starts a
     * fresh one.
     */
    notSaved: 'Nothing you type here is stored anywhere, so this conversation lives in this tab and goes when it does.',
    /**
     * What the meter under the transcript is, said in words for the people
     * who cannot see it.
     *
     * Deliberately not a number. The bar is deliberately not a number either
     * (AskMentor.tsx), and an `aria-label` reading "6 of 10 disclosure" would
     * hand exactly the score the drawing refuses to draw to the people most
     * likely to be reading carefully.
     *
     * It says *this level today* rather than *this conversation*, because the
     * bar is filled by whichever of two limits is further along and the one
     * that usually is belongs to the level and the day, not to the chat.
     */
    depth: 'How far questions have gone on this level today',
    /**
     * Shown while it is still possible to ask: this one is nearly over.
     *
     * The whole reason the meter exists is that a conversation should be
     * visibly finite before it ends rather than at the end, and the meter
     * alone is a shape nobody has been taught to read yet. This is the
     * sentence that teaches it, and it arrives once — with one message left,
     * or less than one full answer's worth of room.
     */
    nearEnd: 'There is not much more that can be said about this level today. The hint ladder below is untouched.',
    /**
     * The disclosure budget is gone and it comes back, which is the version
     * of this sentence a learner can plan around.
     *
     * It says *this level today* and not *this conversation*, because that is
     * what the Worker counts: the budget is summed over one account on one
     * level across a rolling day, so starting a second conversation does not
     * reopen it and the sentence must not imply that it might.
     *
     * Three rules. Which thing ran out: this level's room to be talked about,
     * not the account's questions and not the deployment. When it lifts:
     * `returnsAt`, ready-made from the same `resetAt` the Worker reports, and
     * supplying no preposition of its own so it reads with "tomorrow" and
     * with "in three hours" alike. What is still there: the ladder. And it
     * does not offer help in place of the answer — it says where the answer
     * is, which is a different sentence.
     */
    spent: (returnsAt: string) =>
      `Questions here have said as much about this level as a day allows. More are available ${returnsAt}. The hint ladder below is untouched, and rung five is where the answer lives.`,
    /**
     * The same ending with no time on it.
     *
     * This is the other way `spent` arrives: the answer that came back went
     * further up the ladder than there was room left for, so the filter held
     * it. There are points left — just not enough for what was about to be
     * said — so there is no reset time to give, and inventing one would be a
     * promise about a wait that is not happening.
     *
     * It names what was asked rather than what the learner did, because they
     * did nothing: which question tips over the remaining room is not
     * something anybody can see in advance.
     */
    spentNoReturn: 'Answering that would have said more about this level than there is room left for, so nothing came through. That is as far as questions go here — the hint ladder below is untouched, and rung five is where the answer lives.',
    /**
     * The other ending, and a different fact: this conversation used its last
     * message rather than the level's room to say more.
     *
     * Two endings and not one, for the reason every other pair on this panel
     * is two: a learner who has reached the end of one conversation and a
     * learner who has had everything a day allows about a level have
     * different things to do next, and one sentence for both would leave both
     * of them guessing. This one says nothing about waiting, because waiting
     * is not what a capped conversation is for.
     */
    capped: 'That was the last question this conversation holds. The hint ladder below is untouched, and rung five is where the answer lives.',
    /**
     * The conversation id was not one the Worker would continue — it belongs
     * to another account, or to this one on another level.
     *
     * Nothing a learner can do through the interface reaches this, so it is a
     * stale tab or a forged id, and neither is worth a dead panel or an
     * apology. It says what happened to the thing on screen, because the
     * transcript visibly goes; it does not say "error", because nothing is
     * broken; and it ends on the one useful fact, which is that asking again
     * works right now.
     */
    reissued: 'That conversation could not be carried on, so what was said in it has gone and a new one has started. Asking again works as it always did.',
  },

  /* --- The price of help (components/play/HintLadder.tsx) ------------------ */

  hints: {
    exhausted: 'No more hints',
    showAnswer: 'Show the answer',
    showFirst: 'Show a hint',
    showAnother: 'Show another hint',
    /**
     * Both cost lines carry the stars themselves mid-sentence, so both are
     * parts. `free` is whether the app has pushed a rung at this learner: the
     * stars are still three, and the sentence has to say why.
     */
    worthWithoutHelp: (stars: ReactNode, free: boolean): ReactNode[] => [
      'This level is worth ',
      stars,
      free ? ', and what you have been shown so far is on the house.' : '.',
    ],
    nowWorth: (stars: ReactNode): ReactNode[] => ['This level is now worth ', stars, '.'],
    /**
     * Said before the first rung is opened, and it is the reason this feature
     * exists. The old panel's only sentence about price was a warning, so a
     * learner who was merely unsure read "asking will cost you" and asked
     * nobody. This one is the offer, and it is stated where the hesitation
     * happens rather than in a help page nobody opens.
     */
    firstFree: 'The first hint is free. Stars only start moving at the second one.',
    /**
     * The adaptive ladder (docs/mentor-plan.md §3.6): a situation the level
     * recognizes has made the first `rungs` rungs cost nothing, because the
     * state the workspace is in already demonstrates what those rungs were
     * going to say.
     *
     * The reason is in the sentence rather than left to be guessed at, and it
     * is the reason rather than a favour. A learner who reads "free" with no
     * account of why starts wondering what the app thinks of them; a learner
     * who reads that they have already shown it knows exactly where they
     * stand, which is the whole point of unlocking them.
     */
    freeThrough: (rungs: number) =>
      rungs === 1
        ? 'The first rung of the ladder costs nothing here: the state your workspace is in already says what it would.'
        : `The first ${num(rungs, 'en')} rungs of the ladder cost nothing here: the state your workspace is in already says what they would.`,
  },

  /* --- Nothing at stake (components/play/SandboxRail.tsx) ------------------ */

  sandbox: {
    blockedTitle: 'Open this practice workspace',
    accessNote: 'Practice has no grading. It requires the same sign-in, course access, and completed prerequisites as the level.',
    label: (world: string) => `Sandbox · ${world}`,
    title: 'A repository to break',
    lede: (level: number) =>
      `This is level ${num(level, 'en')}’s workspace, opened with the grading switched off. Nothing here is checked, nothing is scored, and nothing you do is recorded anywhere.`,
    note: 'It is a real Linux and a real git. Type anything. If you wreck it, press Reset and it comes back exactly as it was.',
    thingsToTry: 'Things to try',
    whenReady: 'When you are ready',
    whenReadyNote:
      'The level asks you to do something specific with these, and grades the repository rather than your keystrokes.',
    playForReal: (level: number) => `Play level ${num(level, 'en')} for real`,
    backToChapter: 'Back to the chapter',
    levelList: 'Level list',
  },
};

/* ── Arabic ───────────────────────────────────────────────────────────────── */

export const ar: typeof en = {
  /**
   * The ladder reads as a ladder: مبتدئ → أول → خبير → رئيسي → متميّز. These
   * are the titles an Arabic-speaking engineer would put on a business card,
   * not descriptions of a pay band. `Lattice` is a company name and stays in
   * Latin (docs/i18n.md §2).
   *
   * The blurbs are verbal nouns rather than verbs — `تغيير`, `مراجعة`,
   * `أتمتة` — which is how Arabic writes a capability without committing to a
   * person or a gender, and is a clause each, as the English is.
   */
  ranks: {
    intern: { name: 'متدرّب', blurb: 'يومك الأول في Lattice. كل شيء جديد.' },
    junior: { name: 'مهندس مبتدئ', blurb: 'تجري تغييرات آمنة على فرع مستقل.' },
    engineer: { name: 'مهندس', blurb: 'تنجز التذاكر كاملة وتثبت عملك بالاختبارات.' },
    senior: { name: 'مهندس أول', blurb: 'تتعافى من الأخطاء وتراجع عمل الآخرين.' },
    owner: { name: 'مسؤول خدمة', blurb: 'تفهم الخدمة وتتحمل مسؤولية تاريخها.' },
    lead: { name: 'قائد تقني', blurb: 'ترفع جودة عمل الفريق وتترك الشيفرة أوضح مما وجدتها.' },
    staff: { name: 'مهندس خبير', blurb: 'تطلق الإصدارات وتتحمل مسؤوليتها حتى في الثالثة فجرًا.' },
    principal: { name: 'مهندس رئيسي', blurb: 'تؤتمت الفحوص وتنقل البيانات من دون فقد شيء.' },
    distinguished: { name: 'مهندس متميّز', blurb: 'تقود الاستجابة لانقطاع الخدمة من أول تنبيه إلى تقرير ما بعد الحادثة.' },
  },

  flags: {
    // A decimal takes the singular in Arabic — `1.4 ثانية`, never `ثوانٍ` —
    // so this one needs no agreement. Lowercase fragment, no full stop, as
    // the English is: Play joins it after a colon.
    levelSprint: (seconds: number) =>
      `أُنجز بعد ${decimal(seconds, 1, 'ar')} ثانية من المستوى السابق، ولا يمكن قراءة مستوى وحلّه بهذه السرعة`,
    tampered: 'سجل التقدم المحفوظ لا يطابق توقيعه',
  },

  verdict: {
    passed: 'تم الاجتياز.',
    notYet: 'ليس بعد.',
    /**
     * Here the count sits in apposition after a definite plural — `الأهداف
     * الـ 5` — which is how Arabic writes a digit against a noun it has
     * already made definite, and which does not inflect between 3–10 and
     * 11–99. One and two still do, and the dual is not optional.
     */
    allHold: (total: number) =>
      plural(total, {
        one: 'الهدف الوحيد متحقق.',
        two: 'الهدفان متحققان.',
        few: `الأهداف الـ ${num(total, 'ar')} كلها متحققة.`,
        many: `الأهداف الـ ${num(total, 'ar')} كلها متحققة.`,
        other: `الأهداف الـ ${num(total, 'ar')} كلها متحققة.`,
      }),
    /**
     * `أي من` takes the genitive, so the dual is `هدفين`, and the counted
     * noun splits properly: `من 5 أهداف` against `من 15 هدفًا`. A single
     * objective is named rather than counted, because `أي من هدف واحد` is
     * not a sentence anybody says.
     */
    noneHold: (total: number, hiddenFailed: boolean) => {
      const head =
        total === 1 ? 'الهدف الوحيد لم يتحقق بعد.' : `لم يتحقق أي من ${objectives(total, true)} بعد.`;
      return hiddenFailed ? head + HIDDEN_AR : head;
    },
    /**
     * Two counts in one sentence, agreeing with different things: the noun
     * belongs to `total` (`من 5 أهداف`, `من 20 هدفًا`) and the predicate to
     * `passed` (`متحقق` / `متحققان` / `متحققة`). A nominal sentence, so that
     * the verb-first agreement rules never come into it.
     */
    someHold: (passed: number, total: number, hiddenFailed: boolean) => {
      const subject = plural(passed, {
        one: 'هدف واحد',
        two: 'هدفان',
        few: num(passed, 'ar'),
        many: num(passed, 'ar'),
        other: num(passed, 'ar'),
      });
      const holds = plural(passed, {
        one: 'متحقق',
        two: 'متحققان',
        few: 'متحققة',
        many: 'متحققة',
        other: 'متحققة',
      });
      const head = `${subject} من ${objectives(total, true)} ${holds}.`;
      return hiddenFailed ? head + HIDDEN_AR : head;
    },
  },

  locked: {
    signin: (level: number) => `يحفظ المستوى ${num(level, 'ar')} تقدّمك في حسابك. سجّل الدخول للمتابعة، وما أنجزته حتى الآن يأتي معك.`,
    /**
     * `أنجز` takes its object in the accusative, which is where the dual
     * shows — `المستويين`, never `المستويان` — and `levelsNamed` above is
     * where that agreement lives. The empty case is phrased without a person
     * in it (`ما يقوم عليه لم يكتمل بعد`), which is both the neutral
     * construction the voice asks for and the truth: nobody failed to do
     * anything, the catalogue lost a level.
     */
    sequence: (required: number[], level: number) => {
      const head = `المستوى ${num(level, 'ar')} مقفل.`;
      if (required.length === 0) return `${head} ما يقوم عليه لم يكتمل بعد.`;
      return `${head} أنجز ${levelsNamed(required)} أولًا.`;
    },
    paywall: (level: number, from: number, to: number) =>
      `المستوى ${num(level, 'ar')} جزء من الدورة الكاملة. افتح المستويات ${num(from, 'ar')}-${num(to, 'ar')} للعبه.`,
    paywallSignedOut: (level: number, from: number, to: number) =>
      `المستوى ${num(level, 'ar')} جزء من الدورة الكاملة. سجّل الدخول وافتح المستويات ${num(from, 'ar')}-${num(to, 'ar')} للعبه.`,
    /**
     * `المسار` for a track, which is also the glossary's word for a
     * filesystem path (docs/i18n.md §5). The collision is tolerable and the
     * alternative is worse: `التخصص` would be right for Act III and wrong for
     * the general track, which is a track too. A path in this product is
     * machine text in a code face, set `dir="ltr"`; this is a sentence about
     * the course with a demonstrative in front of it, and no reader meets the
     * two in the same place.
     */
    paywallTrack: (level: number, count: number) =>
      `المستوى ${num(level, 'ar')} جزء من الدورة الكاملة. افتح ${pathLevels(count)} في هذا المسار للعبه.`,
    paywallTrackSignedOut: (level: number, count: number) =>
      `المستوى ${num(level, 'ar')} جزء من الدورة الكاملة. سجّل الدخول وافتح ${pathLevels(count)} في هذا المسار للعبه.`,
  },

  nudge: {
    notStarted: {
      iris: {
        text: 'لا تعرف من أين تبدأ؟ تستطيع Iris أن تخطو معك الخطوة الأولى؛ تقترح كل أمر، وأنت تقرر هل يُنفَّذ.',
        label: 'ابدأ مع Iris',
      },
      hint: {
        text: 'لا تعرف من أين تبدأ؟ أول تلميح يدلّك على الطريق، وهو مجاني هذه المرة.',
        label: 'اعرض التلميح',
      },
    },
    ladderSpent: {
      text: 'الحل ظاهر على الشاشة، لكن المستوى لم يُجتز بعد. أعد مساحة العمل إلى البداية ونفذ الخطوات واحدة تلو الأخرى، أو تخطّ المستوى وعد إليه لاحقًا.',
      label: 'تخطّه الآن',
    },
    neverChecked: {
      // The button is named as the learner sees it, which is why the quoted
      // phrase is the same one play.ts puts on it.
      text: 'لم تتحقق من عملك في هذا المستوى بعد. اضغط «تحقّق من عملي» لفحص الحالة الحالية ومعرفة الجزء الناقص. لن تخسر نجمة ولن ينكشف الحل.',
      label: 'تحقّق الآن',
    },
    sameResult: {
      text: 'تحققت من عملك 3 مرات والنتيجة لم تتغير. سنعرض لك أول تلميح مجانًا؛ فهو يوجهك بسؤال ولا يكشف الإجابة.',
      label: 'اعرض التلميح',
    },
    errorStreak: {
      text: 'انتهت أوامرك الأخيرة بأخطاء. هل تحتاج إلى تلميح يوجهك؟ لن يؤثر في نتيجتك هذه المرة.',
      label: 'اعرض التلميح',
    },
    goneQuiet: {
      text: 'لم يتحقق أي هدف بعد. سنعرض لك تلميحًا يحدد أين تنظر، من دون أن يكشف الحل أو يؤثر في نتيجتك.',
      label: 'اعرض التلميح',
    },
    dismiss: 'ليس الآن',
  },

  clear: {
    levelWorld: (level: number, world: string) => `المستوى ${num(level, 'ar')} · ${world}`,
    checkpointWorld: (world: string) => `محطة · ${world}`,
    title: 'أنجزت المستوى',
    checkpointTitle: 'اجتزت المحطة',
    // `نقطة خبرة` for 0, 1, 2, 11 and up; `نقاط خبرة` for 3–10. The figure
    // beside it is a counter that animates, so the unit is its own string
    // and has to agree with the final value on its own.
    xpUnit: (xp: number) =>
      plural(xp, {
        one: 'نقطة خبرة',
        two: 'نقطتا خبرة',
        few: 'نقاط خبرة',
        many: 'نقطة خبرة',
        other: 'نقطة خبرة',
      }),
    xpAlreadyEarned: 'نقاط خبرة · حصلت عليها سابقًا',
    rank: 'الرتبة',
    meterLabel: 'التقدم نحو الرتبة التالية',
    rankReached: (rank: string) => `بلغت ${rank}`,
    toNextRank: (xp: number, rank: string) =>
      `${num(xp, 'ar')} ${plural(xp, {
        one: 'نقطة',
        two: 'نقطتا',
        few: 'نقاط',
        many: 'نقطة',
        other: 'نقطة',
      })} خبرة حتى ${rank}`,
    topRank: 'بلغت أعلى رتبة',
    noteThreeStars: 'ثلاث نجوم. أكملت المستوى دون فتح تلميح.',
    noteThreeStarsFree: 'ثلاث نجوم، والمساعدة التي أخذتها كانت مجانية. وكانت ستبقى كذلك.',
    // The direct object of `استخدمت` is accusative, which is where the
    // dual and the 11–99 form show: `تلميحًا واحدًا`, `تلميحين`, `15 تلميحًا`.
    noteTwoStars: (hintsUsed: number) =>
      `استخدمت ${plural(hintsUsed, {
        one: 'تلميحًا واحدًا',
        two: 'تلميحين',
        few: `${num(hintsUsed, 'ar')} تلميحات`,
        many: `${num(hintsUsed, 'ar')} تلميحًا`,
        other: `${num(hintsUsed, 'ar')} تلميح`,
      })}. أعد اللعب بلا تلميحات لتنال ثلاث نجوم.`,
    noteOneStar: 'استخدمت خطوة الحل من سلسلة التلميحات. عد لاحقًا وحاول إنجازه من الذاكرة.',
    noteCheckpoint: (world: string) => `تحقّقت كل المتطلبات. شارة «${world}» صارت لك.`,
    readDebrief: 'اقرأ الخلاصة كاملة',
    chapterHandover: (level: number, world: ReactNode, minutes: number): ReactNode[] => [
      `المستوى ${num(level, 'ar')} يبدأ فصلًا جديدًا: `,
      world,
      `. أمامك مقدمة تستغرق ${num(minutes, 'ar')} ${
        minutes >= 3 && minutes <= 10 ? 'دقائق' : 'دقيقة'
      } تشرح موضوع الفصل وأهميته. لا تتضمن أوامر، ويمكنك اجتياز المستوى من دونها.`,
    ],
    nextWorld: (world: string) => `التالي · ${world}`,
    straightToLevel: (level: number) => `مباشرة إلى المستوى ${num(level, 'ar')}`,
    nextLevel: (level: number) => `التالي · المستوى ${num(level, 'ar')}`,
    everyLevelCleared: 'أنجزت كل المستويات',
    levelList: 'قائمة المستويات',
    stayHere: 'ابقَ هنا',
  },

  /**
   * `الخلاصة` is the glossary's word for the debrief (docs/i18n.md §5), and
   * this heading is a question about the route rather than about the result:
   * `كيف سار الأمر` — how it went — because `إلى أين ذهب هذا` would be a
   * literal rendering of an English idiom and nobody says it.
   *
   * `استغرق` takes its object in the accusative, which is where the dual and
   * the 11–99 form show; `arChecks` above is where that agreement lives.
   */
  debrief: {
    postmortem: {
      open: 'اكتب تحليلًا لما بعد الحادثة في سطرين (اختياري)',
      note: 'بلا لوم وخاص بك: عن العمل لا عنك. يبقى في هذا المتصفح، ولا شيء يقيّمه. صياغته بكلماتك هي ما يحوّل فحصًا فاشلًا إلى شيء تحتفظ به.',
      wrong: 'ما الذي فشل أولًا؟',
      changed: 'ما الذي غيّره؟',
      saved: 'محفوظ في هذا المتصفح.',
      unsaved: 'تعذّر على هذا المتصفح حفظه.',
    },
    heading: 'كيف سار الأمر',
    checks: (n: number) => `استغرق الأمر ${arChecks(n)}.`,
    /**
     * عنوان المستوى السابق يأتي مترجمًا من الملف المرافق للمهمة، فيدخل في
     * الجملة نفسها لا بجانبها. وحين لا يصل عنوان — الرقم معروف والاسم لم
     * يصل بعد — تسمّي الجملة المستوى بالرقم وحده وتبقى جملة.
     */
    earlier: (level: number, title?: string) =>
      title
        ? `مررت بهذا من قبل، في المستوى ${num(level, 'ar')}، ${title}. سار الأمر بالطريقة نفسها حينها.`
        : `مررت بهذا من قبل، في المستوى ${num(level, 'ar')}. سار الأمر بالطريقة نفسها حينها.`,
    retrieval: 'من موضع سابق في الدورة',
  },

  /**
   * السؤال بكلمات المتعلّم نفسه، وما يحيط به من حدود.
   *
   * كان هنا نصفان: مرشد مجاني يقرأ حالة مساحة العمل ويقول عنها جملة في كل
   * ضغطة، وهذا. أُلغي الأول (docs/mentor.md)، ومعه أربعة أسماء أزرار وتسع
   * وثلاثون صياغة كانت تعيش في هذه الكتلة.
   *
   * ما بقي كله عن حدّ: حساب، أو حصّة، أو مفتاح لم يُفعَّل، أو ردّ لم يُسمح
   * بخروجه. وهذه هي المواضع التي تنزلق فيها الصياغة، لأن الجملة الصادقة
   * والجملة الدفاعية تتشابهان. ثلاث قواعد، هي نفسها التي استقر عليها ملف
   * التحقّق، وصارت أثقل الآن إذ لا شيء يلتقط المحاولة الفاشلة: سمِّ الحدّ
   * الذي رفض، وقل متى يعود إن كان يعود، واختم بما بقي قائمًا. «نفدت» وحدها
   * تُقرأ اتهامًا لا ميزانية.
   *
   * وقاعدة رابعة من هذه المرحلة: لا تُقدّم أيّ من هذه الجمل مساعدةً بديلة عن
   * الإجابة. جملة فشل تتنكّر في هيئة إشارة هي ادّعاء بأن المحاولة نجحت.
   *
   * `gh`، `git` و`HTTP` تبقى بالحروف اللاتينية: هي ما يكتبه المتعلّم في
   * الـ terminal، وترجمتها تعني أن يبحث عن كلمة لا توجد على لوحة المفاتيح
   * (docs/i18n.md §2).
   *
   * `اليوم` في سطر الحصّة مقصود: النافذة يومية متدحرجة، والمتعلّم يقرأ رقمًا
   * لا يعني شيئًا ما لم يعرف مدّته.
   *
   * منذ 2026-09-28 اسم من يُسأل Iris (docs/iris.md)، بالحروف اللاتينية كما
   * يُكتب Kite و Lattice: الاسم لا يُترجم ولا يُنقل حرفًا (docs/i18n.md). ولم
   * يُحدَّد له جنس بعد، فلا يأتي هنا فاعلًا لفعل: مفعولًا به («اسأل Iris»)
   * أو مضافًا إليه («جواب Iris») أو بعد حرف جرّ منفصل («من Iris»)، ولا
   * يُلصق به حرف (`لـ`، `بـ`).
   */
  mentor: {
    askModel: 'أخبر Iris أين تعثّرت',
    send: 'اسأل Iris',
    thinking: 'جارٍ التفكير…',
    signedOut: 'طرح سؤال على Iris بكلماتك يحتاج إلى حساب. أمّا سلسلة التلميحات أدناه فلا تحتاج إليه.',
    signIn: 'سجّل الدخول',
    /**
     * رقمان في جملة واحدة يوافقان شيئين مختلفين: المبتدأ يوافق `used`
     * (`سؤال واحد` / `سؤالان` / الرقم مجرّدًا)، والمعدود بعد `من` يوافق
     * `limit` في حالة الجرّ (`سؤالين`, `6 أسئلة`). الفعل مبني للمجهول
     * ومتقدّم، فيبقى مفردًا مهما كان العدد.
     */
    allowance: (used: number, limit: number) => {
      const subject = plural(used, {
        one: 'سؤال واحد',
        two: 'سؤالان',
        few: num(used, 'ar'),
        many: num(used, 'ar'),
        other: num(used, 'ar'),
      });
      return `استُخدم اليوم ${subject} من ${arQuestions(limit, true)}.`;
    },
    /**
     * حصّة هذا الحساب نفسه نفدت. `returnsAt` يصل جاهزًا — مدّة أو يوم —
     * فتنتهي الجملة به ولا تضيف إليه حرف جرّ من عندها، لتصحّ مع «غدًا» ومع
     * «بعد ثلاث ساعات». وهو الفشل الوحيد هنا الذي يستطيع المتعلّم أن يرتّب
     * وقته عليه، فلا يصحّ أن يُقال بالكلمات نفسها التي تُقال بها البقية.
     */
    quota: (returnsAt: string) =>
      `نفدت أسئلة اليوم. السؤال التالي متاح ${returnsAt}. سلسلة التلميحات أدناه لا يمسّها هذا.`,
    /**
     * سقف النشر نفسه، لا حصّة المتعلّم. الحالتان تصلان برمز واحد، وأن يُقال
     * لأحدهم إنه استنفد أسئلته وهو لم يستعمل منها شيئًا هو أسوأ ما يمكن أن
     * يقوله هذا السطر: المنتج يحمّل المتعلّم ميزانيته هو.
     */
    paused: 'الأسئلة متوقّفة هنا مؤقتًا — ليست حصّتك، ولم يُخصم منك شيء. سلسلة التلميحات أدناه لا يمسّها هذا.',
    freeTier: 'الدورة كاملة تضم بقية المستويات وحدًّا أوسع من الأسئلة هنا.',
    /**
     * 403 لحساب لا يأتي معه المرشد.
     *
     * رفض مثل الذي تحته، ولا يجوز أن يُقرأ مثله: لا شيء معطّل ولا شيء
     * مكسور، فالميزة تعمل في هذا المستوى لكل من اشترى الدورة. ما على هذه
     * الجملة إلا أن تسمّي الباب دون أن تجعل سلسلة التلميحات تبدو جائزة
     * ترضية — فتقول ما هي عليه فعلًا: خمس درجات مكتوبة في كل مستوى، وهي ما
     * تُحتسب النجوم مقابله.
     */
    courseOnly: 'خطوات Iris المصمَّمة تأتي مع الدورة كاملة، في المستويات التي لها فيها خطوات. وسلسلة التلميحات أدناه لك دائمًا، وهي خمس درجات في كل مستوى.',
    /**
     * 429، وهو الرفض الوحيد في هذه اللوحة الذي لا يعود.
     *
     * استُنفد كل ما تتضمّنه الدورة هنا. تقولها صريحةً دون تلطيف، لأن الخطأ
     * الذي وُجدت هذه الجملة لتفاديه هو أن ينتظر أحدهم الغد — ولا تذكر مالًا،
     * فذلك شأن الخادم لا شيء يوضع أمام متعلّم في منتصف مستوى.
     */
    exhausted: 'هذه كل المساعدة المتاحة لهذا الحساب. وسلسلة التلميحات لا يمسّها هذا، وهي خمس درجات في كل مستوى.',
    /** 403: لا مفتاح، أو مستوى تقييم، أو مستوى ليس في هذه الفئة. */
    offline: 'الأسئلة بكلماتك ليست جزءًا من هذا المستوى. سلسلة التلميحات أدناه تعمل كما كانت دائمًا.',
    /**
     * وصل ردّ ومنعه المرشّح. يقول إن ردًّا كان هناك، لأنه كان، ولا يقول ما
     * فيه، لأن ما يمنعه المرشّح هو حلّ المستوى. «اسأل عن الجزء الذي تعثّرت
     * فيه» نصيحة عن السؤال لا عن المستوى، فليست تلميحًا.
     */
    held: 'وصل ردّ من Iris وكان يكشف من هذا المستوى أكثر مما هو مسموح، فلم يُعرض، ولم يُخصم منك شيء. السؤال عن الجزء الذي تعثّرت فيه وحده يمرّ عادةً.',
    /**
     * النموذج نفسه امتنع عن السؤال، وهذه واقعة أخرى: لا ردّ محجوز حتى
     * يُضيَّق السؤال إليه، فالمخرج هو صياغة السؤال صياغة أخرى.
     */
    declined: 'بقي هذا السؤال بلا جواب من Iris، ولم يُخصم منك شيء. أن تقول ما جرّبته وما حدث بدل ذلك يأتي بجواب عادةً.',
    /** 409. الفشل الوحيد هنا الذي ينتهي من تلقاء نفسه، فالجملة تقول ذلك. */
    inFlight: 'سؤال سابق لك ما زال قيد الإجابة عند Iris — في هذا التبويب أو في غيره. انتظر عودته قبل أن تسأل من جديد.',
    /**
     * الشبكة، أو محاولة ضاعت في الطرف الآخر. الفشل الوحيد الذي لا يستطيع
     * تسمية ما جرى، لأنه لم يصل شيء يسمّيه، فيقول ما يعرفه: لم يُخصم شيء،
     * وإعادة السؤال مجانية.
     */
    failed: 'لم يصل أي رد، ولم يُخصم منك شيء. أعد السؤال، أو خذ السلسلة أدناه؛ تلك لا تغادر التبويب أصلًا.',
    /**
     * مَن قال أيّ دور، لقارئ الشاشة وحده. كان `answered` عنوانًا ظاهرًا فوق
     * الردّ الوحيد؛ صار السجلّ يؤدي عمله، إذ تقف كلمات المتعلّم فوق الجواب
     * عنها مباشرة، فصارت وصفًا مخفيًّا يقول مَن أجاب. «جواب» لا «ردّ»، لأن
     * «ردّ Iris» تُقرأ فعلًا مذكّرًا كما تُقرأ اسمًا.
     */
    youAsked: 'سألت',
    answered: 'جواب Iris',
    /**
     * الوعد لا نتيجته. لا شيء مما يُكتب هنا يُحفظ في أي مكان، وضياع المحادثة
     * عند إعادة التحميل هو شكل هذا الوعد من الداخل. لو قيلت مقلوبةً لقُرئت
     * عيبًا يُحتال عليه، وأول ما تعلّمه أن إعادة التحميل تبدأ محادثة جديدة.
     */
    notSaved: 'لا يُحفظ شيء مما تكتبه هنا في أي مكان، فهذه المحادثة تعيش في هذا التبويب وتذهب بذهابه.',
    /**
     * وصف الشريط بالكلمات لمن لا يراه — بلا أرقام، للسبب نفسه الذي لا يُرسم
     * به رقم. ويقول «هذا المستوى اليوم» لا «هذه المحادثة»، لأن الشريط يمتلئ
     * بأبعد الحدّين، والأبعد عادةً حدُّ المستوى واليوم لا حدُّ المحادثة.
     */
    depth: 'إلى أين بلغت الأسئلة في هذا المستوى اليوم',
    /**
     * تُقال والسؤال ما زال ممكنًا: هذه المحادثة أوشكت. الشريط وحده شكل لم
     * يتعلّمه أحد بعد، وهذه الجملة هي التي تعلّمه، وتصل مرة واحدة.
     */
    nearEnd: 'لم يبقَ اليوم كثيرٌ مما يمكن قوله عن هذا المستوى. سلسلة التلميحات أدناه لا يمسّها هذا.',
    /**
     * نفد رصيد ما يمكن كشفه عن هذا المستوى، وهو يعود. تقول «هذا المستوى
     * اليوم» لا «هذه المحادثة»، لأن هذا ما يعدّه الـ Worker: مجموع على حساب
     * واحد ومستوى واحد على مدى يوم متدحرج، فبدء محادثة أخرى لا يفتحه، ولا
     * يصحّ أن توحي الجملة بغير ذلك. `returnsAt` يصل جاهزًا فلا تضيف إليه
     * حرف جرّ من عندها.
     */
    spent: (returnsAt: string) =>
      `قالت الأسئلة هنا عن هذا المستوى كل ما يسمح به يوم واحد. المزيد متاح ${returnsAt}. سلسلة التلميحات أدناه لا يمسّها هذا، والحل نفسه في درجتها الخامسة.`,
    /**
     * النهاية نفسها بلا موعد: جاء ردٌّ يصعد في السلسلة أبعد مما بقي له من
     * رصيد فمنعه المرشّح. ما زال في الرصيد بقية، لكنها لا تكفي ما كان
     * سيُقال، فلا موعد يُذكر، واختراع موعد وعدٌ بانتظار لا يحدث. وتسمّي ما
     * جرى للسؤال لا ما فعله المتعلّم، لأنه لم يفعل شيئًا: أيّ سؤال يتجاوز
     * ما بقي ليس مما يُرى مقدّمًا.
     */
    spentNoReturn: 'الإجابة عن ذلك كانت ستكشف من هذا المستوى أكثر مما بقي له، فلم يصل شيء. هذا أقصى ما تبلغه الأسئلة هنا — وسلسلة التلميحات أدناه لا يمسّها هذا، والحل نفسه في درجتها الخامسة.',
    /**
     * النهاية الأخرى وواقعة مختلفة: نفدت رسائل هذه المحادثة لا رصيد
     * المستوى. نهايتان لا واحدة، لأن من بلغ آخر محادثته ومن نال كل ما يسمح
     * به اليوم عن المستوى لا يفعلان الشيء نفسه بعدها. ولا ذكر للانتظار هنا،
     * فالانتظار ليس ما تُعالَج به محادثة بلغت حدّها.
     */
    capped: 'كان ذلك آخر سؤال تحتمله هذه المحادثة. سلسلة التلميحات أدناه لا يمسّها هذا، والحل نفسه في درجتها الخامسة.',
    /**
     * معرّف المحادثة ليس مما يواصله الـ Worker — لحساب آخر، أو لهذا الحساب
     * في مستوى آخر. لا يبلغه المتعلّم من الواجهة، فهو تبويب قديم أو معرّف
     * مصطنع، وليس أيّ منهما جديرًا بلوحة ميتة ولا باعتذار. تقول ما جرى لما
     * على الشاشة، ولا تقول «خطأ»، وتختم بأن السؤال من جديد يعمل الآن.
     */
    reissued: 'تعذّر متابعة تلك المحادثة، فذهب ما قيل فيها وبدأت أخرى. السؤال من جديد يعمل كما كان.',
  },

  hints: {
    exhausted: 'لا مزيد من التلميحات',
    showAnswer: 'اعرض الحل',
    showFirst: 'اعرض تلميحًا',
    showAnother: 'اعرض تلميحًا آخر',
    worthWithoutHelp: (stars: ReactNode, free: boolean): ReactNode[] => [
      'قيمة هذا المستوى ',
      stars,
      free ? '، والتلميحات التي عرضناها حتى الآن مجانية.' : '.',
    ],
    nowWorth: (stars: ReactNode): ReactNode[] => ['هذا المستوى يساوي الآن ', stars, '.'],
    firstFree: 'التلميح الأول مجاني. النجوم لا تتحرك إلا عند الثاني.',
    // `أول` مذكّر، فالفعل قبله مذكّر مهما كان المعدود بعده: `لن يكلّفك أول
    // درجتين`. والجملة تقول السبب لا المنّة — من يقرأ «مجاني» بلا سبب يبدأ
    // يتساءل عمّا يظنّه به التطبيق.
    freeThrough: (rungs: number) =>
      `لن يكلّفك ${plural(rungs, {
        one: 'أول درجة',
        two: 'أول درجتين',
        few: `أول ${num(rungs, 'ar')} درجات`,
        many: `أول ${num(rungs, 'ar')} درجة`,
        other: `أول ${num(rungs, 'ar')} درجة`,
      })} من السُلّم شيئًا هنا: ما فيها معروف من حالة مساحة عملك أصلًا.`,
  },

  sandbox: {
    blockedTitle: 'افتح مساحة التدريب هذه',
    accessNote: 'التدريب بلا تقييم. يتطلب تسجيل الدخول وإتاحة الدورة وإكمال المتطلبات السابقة نفسها التي يتطلبها المستوى.',
    label: (world: string) => `مساحة التجربة · ${world}`,
    title: 'مساحة آمنة للتجربة',
    lede: (level: number) =>
      `هذه مساحة عمل المستوى ${num(level, 'ar')} من دون تقييم. لن تُفحص تغييراتك أو تُحتسب أو تُحفظ في سجل تقدمك.`,
    note: 'هذه بيئة Linux حقيقية وبداخلها Git حقيقي. جرّب ما تشاء، وإذا تعطلت المساحة فاضغط «إعادة الضبط» لتعود كما كانت.',
    thingsToTry: 'أشياء لتجربها',
    whenReady: 'حين تكون مستعدًا',
    whenReadyNote: 'سيطلب منك المستوى استخدام هذه الأدوات للوصول إلى نتيجة محددة، وسيقيّم حالة المستودع لا خطواتك.',
    playForReal: (level: number) => `العب المستوى ${num(level, 'ar')} فعليًا`,
    backToChapter: 'العودة إلى الفصل',
    levelList: 'قائمة المستويات',
  },
};

/* ── German ───────────────────────────────────────────────────────────────── */

/** "3", "3 und 5", "3, 5 und 7" — German puts no comma before the last `und`. */
function listDe(parts: string[]): string {
  if (parts.length < 3) return parts.join(' und ');
  return `${parts.slice(0, -1).join(', ')} und ${parts[parts.length - 1]}`;
}

const HIDDEN_DE = ' Eine verdeckte Prüfung ist fehlgeschlagen; sieh dir die Ziele an.';

/**
 * German. Job titles stay in English, as they do on German job boards and in
 * German engineering teams: a "Senior Engineer" is what the reader will be
 * called, and a translated title would be a title nobody has.
 */
export const de: typeof en = {
  ranks: {
    intern: { name: 'Intern', blurb: 'Erster Tag bei Lattice. Alles ist neu.' },
    junior: { name: 'Junior Engineer', blurb: 'Kann Code sicher auf einem Branch ändern.' },
    engineer: { name: 'Engineer', blurb: 'Bringt Tickets von Anfang bis Ende durch, Tests inklusive.' },
    senior: { name: 'Senior Engineer', blurb: 'Kommt aus allem wieder heraus. Reviewt andere.' },
    owner: { name: 'Service Owner', blurb: 'Verantwortet einen Dienst und seine Geschichte.' },
    lead: { name: 'Tech Lead', blurb: 'Reviewt die Arbeit anderer. Hinterlässt die Codebasis klarer.' },
    staff: { name: 'Staff Engineer', blurb: 'Schneidet das Release und steht um drei Uhr nachts dafür gerade.' },
    principal: { name: 'Principal Engineer', blurb: 'Automatisiert die Prüfungen. Verschiebt Daten, ohne welche zu verlieren.' },
    distinguished: { name: 'Distinguished Engineer', blurb: 'Führt einen Ausfall vom Alarm bis zum Postmortem.' },
  },
  flags: {
    levelSprint: (seconds) =>
      `es wurde ${decimal(seconds, 1, 'de')} Sekunden nach dem vorigen Level geschafft, und so schnell kann man ein Level nicht lesen und lösen`,
    tampered: 'der gespeicherte Fortschritt passte nicht zu seiner Signatur',
  },
  verdict: {
    passed: 'Bestanden.',
    notYet: 'Noch nicht.',
    allHold: (total) => `Alle ${num(total, 'de')} Ziele sind erfüllt.`,
    noneHold: (total, hiddenFailed) =>
      `Noch keins der ${num(total, 'de')} Ziele ist erfüllt.` + (hiddenFailed ? HIDDEN_DE : ''),
    someHold: (passed, total, hiddenFailed) =>
      `${num(passed, 'de')} von ${num(total, 'de')} Zielen sind erfüllt.` + (hiddenFailed ? HIDDEN_DE : ''),
  },
  locked: {
    signin: (level) =>
      `Level ${num(level, 'de')} speichert deinen Fortschritt in deinem Konto. Melde dich an, um weiterzumachen – die Level, die du schon geschafft hast, nimmst du mit.`,
    sequence: (required, level) => {
      const head = `Level ${num(level, 'de')} ist gesperrt.`;
      if (required.length === 0) return `${head} Etwas, worauf es aufbaut, ist noch nicht geschafft.`;
      return `${head} Schaff zuerst Level ${listDe(required.map((l) => num(l, 'de')))}.`;
    },
    paywall: (level, from, to) =>
      `Level ${num(level, 'de')} gehört zum vollen Kurs. Schalte die Level ${num(from, 'de')}–${num(to, 'de')} frei, um es zu spielen.`,
    paywallSignedOut: (level, from, to) =>
      `Level ${num(level, 'de')} gehört zum vollen Kurs. Melde dich an und schalte die Level ${num(from, 'de')}–${num(to, 'de')} frei, um es zu spielen.`,
    paywallTrack: (level, count) =>
      `Level ${num(level, 'de')} gehört zum vollen Kurs. Schalte ${count === 1 ? 'das Level' : `die ${num(count, 'de')} Level`} dieses Pfads frei, um es zu spielen.`,
    paywallTrackSignedOut: (level, count) =>
      `Level ${num(level, 'de')} gehört zum vollen Kurs. Melde dich an und schalte ${count === 1 ? 'das Level' : `die ${num(count, 'de')} Level`} dieses Pfads frei, um es zu spielen.`,
  },
  nudge: {
    notStarted: {
      iris: {
        text: 'Nicht sicher, wo du anfangen sollst? Iris kann den ersten Schritt mit dir gehen – sie schlägt jeden Befehl vor, und du entscheidest, ob er läuft.',
        label: 'Mit Iris anfangen',
      },
      hint: {
        text: 'Nicht sicher, wo du anfangen sollst? Der erste Hinweis zeigt dir den Weg – und dieser geht aufs Haus.',
        label: 'Zeig es mir',
      },
    },
    ladderSpent: {
      text: 'Die Lösung steht auf dem Bildschirm, und das Level besteht immer noch nicht. Setz den Arbeitsbereich zurück und geh es Schritt für Schritt durch, oder lass dieses liegen und komm später wieder.',
      label: 'Vorerst überspringen',
    },
    neverChecked: {
      text: 'In diesem Level wurde noch nichts geprüft. „Meine Arbeit prüfen“ bewertet, was jetzt da ist, und nennt den Teil, der noch nicht fertig ist – das kostet nichts und verrät nichts.',
      label: 'Prüfen',
    },
    sameResult: {
      text: 'Drei Prüfungen, dasselbe Ergebnis. Ein Hinweis ist hier eine Frage, nicht die Lösung – und dieser geht aufs Haus.',
      label: 'Zeig es mir',
    },
    errorStreak: {
      text: 'Jeder Befehl endet seit einer Weile mit einem Fehler. Willst du einen Tipp? Diesmal kostet er nichts.',
      label: 'Zeig es mir',
    },
    goneQuiet: {
      text: 'In diesem Level besteht immer noch nichts. Gezeigt zu bekommen, wo du hinschauen sollst, kostet hier nichts, und lösen darfst du das Level trotzdem selbst.',
      label: 'Zeig es mir',
    },
    dismiss: 'Jetzt nicht',
  },
  clear: {
    levelWorld: (level, world) => `Level ${num(level, 'de')} · ${world}`,
    checkpointWorld: (world) => `Checkpoint · ${world}`,
    title: 'Level geschafft',
    checkpointTitle: 'Checkpoint bestanden',
    xpUnit: () => 'XP',
    xpAlreadyEarned: 'XP · schon verdient',
    rank: 'Rang',
    meterLabel: 'Fortschritt zum nächsten Rang',
    rankReached: (rank) => `${rank} erreicht`,
    toNextRank: (xp, rank) => `${num(xp, 'de')} XP bis ${rank}`,
    topRank: 'Höchster Rang erreicht',
    noteThreeStars: 'Drei Sterne. Du hast das Level abgeschlossen, ohne einen Hinweis zu öffnen.',
    noteThreeStarsFree: 'Drei Sterne, und die Hilfe, die du genommen hast, war kostenlos. Das war sie von Anfang an.',
    noteTwoStars: (hintsUsed) =>
      `${num(hintsUsed, 'de')} ${hintsUsed === 1 ? 'Hinweis' : 'Hinweise'} genutzt. Spiel es ohne Hinweise noch einmal für drei Sterne.`,
    noteOneStar: 'Mit der Lösungsstufe gelöst. Komm später wieder und mach es aus dem Gedächtnis.',
    noteCheckpoint: (world) => `Jede Anforderung ist erfüllt. Das Abzeichen für ${world} gehört dir.`,
    readDebrief: 'Die ganze Nachbesprechung lesen',
    chapterHandover: (level, world, minutes) => [
      `Mit Level ${num(level, 'de')} beginnt ein neues Kapitel: `,
      world,
      `. Dazu gibt es einen Text für ${num(minutes, 'de')} Minuten darüber, was es ist und warum es wichtig ist – keine Befehle, nichts, was du zum Bestehen des Levels brauchst.`,
    ],
    nextWorld: (world) => `Weiter · ${world}`,
    straightToLevel: (level) => `Direkt zu Level ${num(level, 'de')}`,
    nextLevel: (level) => `Weiter · Level ${num(level, 'de')}`,
    everyLevelCleared: 'Alle Level geschafft',
    levelList: 'Levelliste',
    stayHere: 'Hierbleiben',
  },
  debrief: {
    postmortem: {
      open: 'Ein Postmortem in zwei Zeilen schreiben (optional)',
      note: 'Ohne Schuldzuweisung und privat: über die Arbeit, nicht über dich. Es bleibt in diesem Browser, und nichts bewertet es. Es in eigenen Worten zu sagen, macht aus einer fehlgeschlagenen Prüfung etwas, das bleibt.',
      wrong: 'Was ging zuerst schief?',
      changed: 'Was hat es geändert?',
      saved: 'In diesem Browser gespeichert.',
      unsaved: 'Dieser Browser konnte es nicht speichern.',
    },
    heading: 'Wie es gelaufen ist',
    checks: (n) => (n === 1 ? 'Es hat eine Prüfung gebraucht.' : `Es hat ${num(n, 'de')} Prüfungen gebraucht.`),
    earlier: (level, title) =>
      title
        ? `Das kam schon einmal vor, in Level ${num(level, 'de')}, ${title}. Damals lief es genauso.`
        : `Das kam schon einmal vor, in Level ${num(level, 'de')}. Damals lief es genauso.`,
    retrieval: 'Aus einem früheren Teil des Kurses',
  },
  mentor: {
    askModel: 'Sag Iris, wo du feststeckst',
    send: 'Iris fragen',
    thinking: 'Denkt nach …',
    signedOut: 'Um Iris in deinen eigenen Worten zu fragen, brauchst du ein Konto. Für die Hinweisleiter unten nicht.',
    signIn: 'Anmelden',
    allowance: (used, limit) => `${num(used, 'de')} von ${num(limit, 'de')} Fragen heute.`,
    quota: (returnsAt) =>
      `Die Fragen für heute sind aufgebraucht. Die nächste gibt es ${returnsAt}. Die Hinweisleiter unten ist davon nicht betroffen.`,
    paused: 'Fragen sind hier im Moment pausiert – das liegt nicht an deinem Kontingent, und nichts davon wurde verbraucht. Die Hinweisleiter unten ist davon nicht betroffen.',
    freeTier: 'Der volle Kurs bringt die übrigen Level und mehr Spielraum hier mit.',
    courseOnly: 'Die vorbereiteten Schritte von Iris gehören zum vollen Kurs, in den Levels, in denen es sie gibt. Die Hinweisleiter unten gehört immer dir, und sie hat in jedem Level fünf Stufen.',
    exhausted: 'Das ist die ganze Hilfe, die dieses Konto hat. Die Hinweisleiter ist nicht betroffen, und sie hat in jedem Level fünf Stufen.',
    offline: 'Fragen in deinen eigenen Worten gehören nicht zu diesem Level. Die Hinweisleiter unten funktioniert wie immer.',
    held: 'Iris hat geantwortet, aber die Antwort hat mehr über dieses Level verraten, als sie darf. Dafür wurde nichts berechnet. Wenn du nach dem einen Teil fragst, an dem du feststeckst, kommt meist eine durch.',
    declined: 'Iris hat diese Frage nicht beantwortet, und dafür wurde nichts berechnet. Wenn du sagst, was du versucht hast und was stattdessen passiert ist, bekommst du meist eine Antwort.',
    inFlight: 'Iris beantwortet noch eine Frage von dir – in diesem Tab oder einem anderen. Warte, bis diese zurückkommt, bevor du erneut fragst.',
    failed: 'Es kam keine Antwort zurück, und dafür wurde nichts berechnet. Frag noch einmal oder nimm die Leiter unten – die verlässt den Tab nie.',
    youAsked: 'Du hast gefragt',
    answered: 'Iris hat geantwortet',
    notSaved: 'Nichts, was du hier tippst, wird irgendwo gespeichert. Dieses Gespräch lebt in diesem Tab und verschwindet mit ihm.',
    depth: 'Wie weit die Fragen zu diesem Level heute gegangen sind',
    nearEnd: 'Über dieses Level lässt sich heute nicht mehr viel sagen. Die Hinweisleiter unten ist unberührt.',
    spent: (returnsAt) =>
      `Die Fragen hier haben über dieses Level so viel gesagt, wie ein Tag erlaubt. Weitere gibt es ${returnsAt}. Die Hinweisleiter unten ist unberührt, und auf Stufe fünf steht die Lösung.`,
    spentNoReturn: 'Darauf zu antworten hätte mehr über dieses Level verraten, als noch Platz ist, deshalb kam nichts durch. Weiter gehen Fragen hier nicht – die Hinweisleiter unten ist unberührt, und auf Stufe fünf steht die Lösung.',
    capped: 'Das war die letzte Frage, die dieses Gespräch fasst. Die Hinweisleiter unten ist unberührt, und auf Stufe fünf steht die Lösung.',
    reissued: 'Dieses Gespräch konnte nicht fortgesetzt werden, deshalb ist das Gesagte weg und ein neues hat begonnen. Erneut fragen funktioniert wie immer.',
  },
  hints: {
    exhausted: 'Keine Hinweise mehr',
    showAnswer: 'Lösung anzeigen',
    showFirst: 'Hinweis anzeigen',
    showAnother: 'Noch einen Hinweis anzeigen',
    worthWithoutHelp: (stars, free) => [
      'Dieses Level ist ',
      stars,
      free ? ' wert, und was dir bisher gezeigt wurde, geht aufs Haus.' : ' wert.',
    ],
    nowWorth: (stars) => ['Dieses Level ist jetzt ', stars, ' wert.'],
    firstFree: 'Der erste Hinweis ist kostenlos. Sterne kostet erst der zweite.',
    freeThrough: (rungs) =>
      rungs === 1
        ? 'Die erste Stufe der Leiter kostet hier nichts: Der Zustand deines Arbeitsbereichs sagt schon, was sie sagen würde.'
        : `Die ersten ${num(rungs, 'de')} Stufen der Leiter kosten hier nichts: Der Zustand deines Arbeitsbereichs sagt schon, was sie sagen würden.`,
  },
  sandbox: {
    blockedTitle: 'Diesen Übungsbereich öffnen',
    accessNote: 'Die Übung hat keine Bewertung. Anmeldung, Kurszugang und abgeschlossene Voraussetzungen gelten wie im Level.',
    label: (world) => `Sandbox · ${world}`,
    title: 'Ein Repository zum Kaputtmachen',
    lede: (level) =>
      `Das ist der Arbeitsbereich von Level ${num(level, 'de')}, geöffnet mit ausgeschalteter Bewertung. Nichts hier wird geprüft, nichts wird gezählt, und nichts, was du tust, wird irgendwo gespeichert.`,
    note: 'Es ist ein echtes Linux und ein echtes git. Tipp, was du willst. Wenn du es kaputtmachst, drück „Zurücksetzen“, und es kommt genau so zurück, wie es war.',
    thingsToTry: 'Zum Ausprobieren',
    whenReady: 'Wenn du so weit bist',
    whenReadyNote: 'Das Level verlangt etwas Bestimmtes damit und bewertet das Repository, nicht deine Tastenanschläge.',
    playForReal: (level) => `Level ${num(level, 'de')} richtig spielen`,
    backToChapter: 'Zurück zum Kapitel',
    levelList: 'Levelliste',
  },
};

const jaHidden = '非公開のチェックが失敗しています。目標の一覧を確認してください。';

export const ja: typeof en = {
  ranks: {
    intern: { name: 'インターン', blurb: 'Latticeでの初日。すべてが新しい。' },
    junior: { name: 'ジュニアエンジニア', blurb: 'ブランチの上で、安全にコードを変更できる。' },
    engineer: { name: 'エンジニア', blurb: 'チケットを最初から最後まで、テストも含めてリリースできる。' },
    senior: { name: 'シニアエンジニア', blurb: 'どんな状況からも立て直せる。他の人をレビューする。' },
    owner: { name: 'サービスオーナー', blurb: 'サービスとその履歴に責任を持つ。' },
    lead: { name: 'テックリード', blurb: '他の人の仕事をレビューし、コードベースをより明快にして残す。' },
    staff: { name: 'スタッフエンジニア', blurb: 'リリースを切り、午前3時にもその責任を負う。' },
    principal: { name: 'プリンシパルエンジニア', blurb: 'チェックを自動化する。データを1件も失わずに移行する。' },
    distinguished: { name: 'ディスティングイッシュトエンジニア', blurb: 'アラートからポストモーテムまで、障害対応を導く。' },
  },
  flags: {
    levelSprint: (seconds) => `前のレベルの${decimal(seconds, 1, 'ja')}秒後にクリアされており、レベルを読んで解くにはあまりに速すぎる`,
    tampered: '保存された進捗の記録が、署名と一致しなかった',
  },
  verdict: {
    passed: '合格しました。',
    notYet: 'まだです。',
    allHold: (total) => `${num(total, 'ja')}個の目標をすべて満たしています。`,
    noneHold: (total, hiddenFailed) =>
      `${num(total, 'ja')}個の目標のうち、まだ満たしているものはありません。` + (hiddenFailed ? jaHidden : ''),
    someHold: (passed, total, hiddenFailed) =>
      `${num(total, 'ja')}個中${num(passed, 'ja')}個の目標を満たしています。` + (hiddenFailed ? jaHidden : ''),
  },
  locked: {
    signin: (level) => `レベル${num(level, 'ja')}以降の進捗は、アカウントに保存されます。続けるにはログインしてください。すでにクリアしたレベルは引き継がれます。`,
    sequence: (required, level) => {
      const head = `レベル${num(level, 'ja')}はロックされています。`;
      if (required.length === 0) return `${head}このレベルの土台になっているものが、まだクリアされていません。`;
      return `${head}先にレベル${required.map((l) => num(l, 'ja')).join('、')}をクリアしてください。`;
    },
    paywall: (level, from, to) => `レベル${num(level, 'ja')}はコース全体に含まれます。プレイするには、レベル${num(from, 'ja')}〜${num(to, 'ja')}をアンロックしてください。`,
    paywallSignedOut: (level, from, to) => `レベル${num(level, 'ja')}はコース全体に含まれます。プレイするには、ログインして、レベル${num(from, 'ja')}〜${num(to, 'ja')}をアンロックしてください。`,
    paywallTrack: (level, count) => `レベル${num(level, 'ja')}はコース全体に含まれます。プレイするには、このトラックの${num(count, 'ja')}レベルをアンロックしてください。`,
    paywallTrackSignedOut: (level, count) => `レベル${num(level, 'ja')}はコース全体に含まれます。プレイするには、ログインして、このトラックの${num(count, 'ja')}レベルをアンロックしてください。`,
  },
  nudge: {
    notStarted: {
      iris: {
        text: 'どこから始めればいいか迷っていますか？最初の一歩はIrisと一緒に踏み出せます。Irisがコマンドを一つずつ提案し、実行するかどうかはあなたが決めます。',
        label: 'Irisと始める',
      },
      hint: {
        text: 'どこから始めればいいか迷っていますか？最初のヒントが道を示してくれます。これは無料です。',
        label: 'ヒントを見る',
      },
    },
    ladderSpent: {
      text: '答えが画面に出ているのに、レベルはまだ合格していません。ワークスペースをリセットして一歩ずつたどってみるか、このレベルはいったん離れて、あとで戻ってきてください。',
      label: 'いったんスキップ',
    },
    neverChecked: {
      text: 'このレベルでは、まだ一度もチェックしていません。「作業をチェック」は、今ある状態を採点して、まだ終わっていない部分を教えてくれます。費用はかからず、答えも明かされません。',
      label: 'チェックする',
    },
    sameResult: {
      text: 'チェックを3回して、結果は同じです。ここでのヒントは答えではなく問いかけで、しかもこれは無料です。',
      label: 'ヒントを見る',
    },
    errorStreak: {
      text: 'しばらく、どのコマンドもエラーで終わっています。手がかりが欲しいですか？今回は無料です。',
      label: 'ヒントを見る',
    },
    goneQuiet: {
      text: 'このレベルは、まだ何も合格していません。どこを見ればいいかを教えてもらうのは、ここでは無料です。レベルを解くのは、これからもあなた自身です。',
      label: 'ヒントを見る',
    },
    dismiss: '今はやめておく',
  },
  clear: {
    levelWorld: (level, world) => `レベル${num(level, 'ja')} · ${world}`,
    checkpointWorld: (world) => `チェックポイント · ${world}`,
    title: 'レベルクリア',
    checkpointTitle: 'チェックポイントに合格',
    xpUnit: () => 'XP',
    xpAlreadyEarned: 'XP · 獲得済み',
    rank: 'ランク',
    meterLabel: '次のランクまでの進捗',
    rankReached: (rank) => `${rank}に到達`,
    toNextRank: (xp, rank) => `${rank}まであと${num(xp, 'ja')} XP`,
    topRank: '最高ランクに到達',
    noteThreeStars: '3つ星。ヒントを開かずにレベルを完了しました。',
    noteThreeStarsFree: '星3つ。使ったヒントは無料でした。最初から、そういう仕組みです。',
    noteTwoStars: (hintsUsed) => `ヒントを${num(hintsUsed, 'ja')}個使いました。ヒントなしでもう一度プレイすると、星3つになります。`,
    noteOneStar: '解答の段を使って解きました。あとでもう一度、記憶を頼りに解いてみましょう。',
    noteCheckpoint: (world) => `すべての要件を満たしました。${world}のバッジを獲得です。`,
    readDebrief: '振り返りを全文読む',
    chapterHandover: (level, world, minutes) => [
      `レベル${num(level, 'ja')}から、新しい章が始まります: `,
      world,
      `。この章が何で、なぜ大切なのかを読む、${num(minutes, 'ja')}分ほどの文章があります。コマンドはなく、レベルをクリアするのに必要なものでもありません。`,
    ],
    nextWorld: (world) => `次へ · ${world}`,
    straightToLevel: (level) => `レベル${num(level, 'ja')}へ直行`,
    nextLevel: (level) => `次へ · レベル${num(level, 'ja')}`,
    everyLevelCleared: 'すべてのレベルをクリア',
    levelList: 'レベル一覧',
    stayHere: 'ここに残る',
  },
  debrief: {
    postmortem: {
      open: '2行のポストモーテムを書く（任意）',
      note: '誰も責めない、あなただけの記録です。対象は作業で、あなた自身ではありません。このブラウザにだけ残り、評価されません。自分の言葉で書くことで、失敗したチェックが身につく学びに変わります。',
      wrong: '最初に何がうまくいかなかった？',
      changed: '何がそれを変えた？',
      saved: 'このブラウザに保存しました。',
      unsaved: 'このブラウザでは保存できませんでした。',
    },
    heading: 'ここまでの流れ',
    checks: (n) => (n === 1 ? 'チェックは1回で済みました。' : `チェックは${num(n, 'ja')}回でした。`),
    earlier: (level, title) =>
      title
        ? `以前、レベル${num(level, 'ja')}「${title}」で同じ場面がありました。そのときも、同じ結果でした。`
        : `以前、レベル${num(level, 'ja')}で同じ場面がありました。そのときも、同じ結果でした。`,
    retrieval: 'コースの前のほうから',
  },
  mentor: {
    askModel: 'どこで詰まっているかをIrisに伝える',
    send: 'Irisに質問',
    thinking: '考え中…',
    signedOut: '自分の言葉でIrisに質問するには、アカウントが必要です。下のヒントの段階は、アカウントなしで使えます。',
    signIn: 'ログイン',
    allowance: (used, limit) => `今日の質問: ${num(limit, 'ja')}問中${num(used, 'ja')}問。`,
    quota: (returnsAt) => `今日の質問は使い切りました。次の質問が使えるのは${returnsAt}です。下のヒントの段階には影響しません。`,
    paused: '質問は現在、一時停止しています。これはあなたの利用枠のせいではなく、消費されたものもありません。下のヒントの段階には影響しません。',
    freeTier: 'コース全体には、残りのレベルと、ここでのより多くの余裕が含まれます。',
    courseOnly: 'Irisの用意されたステップは、ステップがあるレベルでコース全体に含まれます。下のヒントの段階はいつでも使え、どのレベルも5段あります。',
    exhausted: 'このアカウントで使えるヘルプは、すべて使い切りました。ヒントの段階は影響を受けず、どのレベルも5段あります。',
    offline: '自分の言葉での質問は、このレベルには含まれていません。下のヒントの段階は、これまでどおり使えます。',
    held: 'Irisは答えましたが、その内容はこのレベルについて、許されている以上のことを明かしていました。そのため、料金はかかっていません。行き詰まっている1点に絞って聞くと、たいてい通ります。',
    declined: 'Irisはその質問には答えませんでした。料金もかかっていません。試したことと、代わりに何が起きたかを伝えると、たいてい答えが返ってきます。',
    inFlight: 'Irisは、あなたの別の質問にまだ回答中です(このタブか、別のタブで)。その回答が戻るのを待ってから、もう一度質問してください。',
    failed: '回答が返ってこず、料金もかかっていません。もう一度質問するか、下のヒントの段階を使ってください。こちらはタブの外には出ません。',
    youAsked: 'あなたの質問',
    answered: 'Irisの回答',
    notSaved: 'ここに入力した内容は、どこにも保存されません。この会話はこのタブの中にだけあり、タブを閉じると消えます。',
    depth: '今日、このレベルについての質問がどこまで進んだか',
    nearEnd: '今日はもう、このレベルについて話せることはあまり残っていません。下のヒントの段階は、そのまま使えます。',
    spent: (returnsAt) => `ここでの質問は、1日で許される範囲まで、このレベルについて語りました。追加の質問が使えるのは${returnsAt}です。下のヒントの段階はそのまま使え、答えは5段目にあります。`,
    spentNoReturn: 'それに答えると、残りの余裕を超えてこのレベルについて明かしてしまうため、何も返しませんでした。ここでの質問はここまでです。下のヒントの段階はそのまま使え、答えは5段目にあります。',
    capped: 'この会話で受け付けられる、最後の質問でした。下のヒントの段階はそのまま使え、答えは5段目にあります。',
    reissued: 'その会話は続けられなかったため、そこで話した内容は消え、新しい会話が始まりました。もう一度質問すれば、これまでどおり使えます。',
  },
  hints: {
    exhausted: 'ヒントはもうありません',
    showAnswer: '答えを表示',
    showFirst: 'ヒントを表示',
    showAnother: 'もう1つヒントを表示',
    worthWithoutHelp: (stars, free) => [
      'このレベルは、',
      stars,
      free ? 'の価値があり、ここまでに見せたものは無料です。' : 'の価値があります。',
    ],
    nowWorth: (stars) => ['このレベルは今、', stars, 'の価値があります。'],
    firstFree: '最初のヒントは無料です。星が減り始めるのは、2つ目からです。',
    freeThrough: (rungs) =>
      rungs === 1
        ? 'ヒントの最初の段は、ここでは無料です。ワークスペースの今の状態が、その内容をすでに語っているからです。'
        : `ヒントの最初の${num(rungs, 'ja')}段は、ここでは無料です。ワークスペースの今の状態が、その内容をすでに語っているからです。`,
  },
  sandbox: {
    blockedTitle: 'この練習用ワークスペースを開く',
    accessNote: '練習は採点されません。ログイン、コースへのアクセス、前提レベルの完了は本番レベルと同じ条件です。',
    label: (world) => `サンドボックス · ${world}`,
    title: '壊してよいリポジトリ',
    lede: (level) => `レベル${num(level, 'ja')}のワークスペースを、採点をオフにした状態で開いています。ここではチェックも採点もされず、あなたの操作はどこにも記録されません。`,
    note: '本物のLinuxと本物のgitです。何でも入力してください。壊してしまっても、「リセット」を押せば、元の状態にそっくり戻ります。',
    thingsToTry: '試してみること',
    whenReady: '準備ができたら',
    whenReadyNote: 'レベルでは、これらを使って具体的な操作をするよう求められ、採点されるのはキー入力ではなくリポジトリです。',
    playForReal: (level) => `レベル${num(level, 'ja')}を本番でプレイ`,
    backToChapter: '章に戻る',
    levelList: 'レベル一覧',
  },
};
